import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Flex, Text, toast } from "@jsq/ds/core";
import { FormTextInput, FormSelect, SubmitButton } from "@jsq/ds/forms";
import { Money } from "~/components/Money";
import { useModifyPaymentStatusMutation } from "~/graphql/supergraphHooks";
import { type TerminalPaymentStatus } from "~/graphql/supergraphTypes";
import { getErrorMessage } from "~/utils/errors";
import * as styles from "./styles_vanilla.css";

interface FormValues {
  paymentId: string;
  status: TerminalPaymentStatus;
  failureReason: string;
}

const validationSchema = Yup.object().shape({
  paymentId: Yup.string().required("Payment ID is required"),
  status: Yup.string().required("Payment status is required"),
  failureReason: Yup.string().when("status", {
    is: "FAILED",
    then: (schema) => schema.required("Failure reason is required when status is FAILED"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const initialValues: FormValues = {
  paymentId: "",
  status: "FAILED",
  failureReason: "",
};

const paymentStatusOptions: Array<{ value: TerminalPaymentStatus; label: string }> = [
  { value: "FAILED", label: "Failed" },
  { value: "SETTLED", label: "Settled" },
  { value: "CANCELED", label: "Canceled" },
];

const ModifyPaymentStatus: React.FC = () => {
  const [submittedPayment, setSubmittedPayment] = useState<any>(null);

  const [modifyPaymentStatus, { loading }] = useModifyPaymentStatusMutation({
    onCompleted: (data) => {
      if (data?.modifyPaymentStatus) {
        setSubmittedPayment(data.modifyPaymentStatus);
        toast.success("Payment status updated successfully");
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleSubmit = async (values: FormValues) => {
    await modifyPaymentStatus({
      variables: {
        modifyPaymentStatusInput: {
          paymentExternalId: values.paymentId,
          status: values.status,
          failureReason: values.failureReason || undefined,
        },
      },
    });
  };

  return (
    <Flex
      data-testid="modify-payment-status-root"
      flexDirection="column"
      className={styles.container}
    >
      <div className={styles.header}>
        <Text variant="regular-xxxl" className={styles.pageTitle}>
          Modify Payment Status
        </Text>
      </div>
      <div className={styles.contentArea} data-testid="modify-payment-status-content">
        <div className={styles.formSection}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values }) => (
              <Form>
                <div className={styles.formFields}>
                  <FormTextInput
                    name="paymentId"
                    label="Payment ID"
                    placeholder="Enter payment external ID"
                    required
                  />
                  <FormSelect
                    name="status"
                    label="Payment Status"
                    placeholder="Select payment status"
                    full
                    required
                  >
                    <FormSelect.Section>
                      {paymentStatusOptions.map((option) => (
                        <FormSelect.Option key={option.value} value={option.value}>
                          {option.label}
                        </FormSelect.Option>
                      ))}
                    </FormSelect.Section>
                  </FormSelect>
                  <FormTextInput
                    name="failureReason"
                    label="Failure Reason"
                    helpText="Only required if marking a payment as failed"
                    placeholder="Payment failure reason"
                    required={values.status === "FAILED"}
                  />
                </div>
                <div className={styles.submitSection}>
                  <SubmitButton isLoading={loading} disabled={loading}>
                    Modify Payment Status
                  </SubmitButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        {submittedPayment && (
          <div className={styles.resultSection}>
            <Text variant="regular-xl" className={styles.resultTitle}>
              Payment Updated Successfully
            </Text>
            <div className={styles.paymentDetails}>
              <Text variant="regular-md" className={styles.detailLabel}>
                Payment ID: <span className={styles.detailValue}>{submittedPayment.id}</span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Status: <span className={styles.detailValue}>{submittedPayment.status}</span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Failure Reason:{" "}
                <span className={styles.detailValue}>{submittedPayment.failureReason}</span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Failed At:{" "}
                <span className={styles.detailValue}>{submittedPayment.failedAt || "N/A"}</span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Amount:{" "}
                <span className={styles.detailValue}>
                  <Money money={submittedPayment.amount} />
                </span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Method: <span className={styles.detailValue}>{submittedPayment.method}</span>
              </Text>
              <Text variant="regular-md" className={styles.detailLabel}>
                Direction:{" "}
                <span className={styles.detailValue}>{submittedPayment.direction}</span>
              </Text>
            </div>
          </div>
        )}
      </div>
    </Flex>
  );
};

export default ModifyPaymentStatus;
export { ModifyPaymentStatus };
